import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaBars, FaTimes, FaInstagram, FaChevronDown } from "react-icons/fa";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const servicesSections = [
    { name: "Travel Planning", hash: "planning" },
    { name: "Booking & Coordination", hash: "booking" },
    { name: "Luxury Travel", hash: "luxury" },
    { name: "Advisory & Digital", hash: "advisory" },
    { name: "Support & Advocacy", hash: "support" },
  ];

  const handleServiceClick = (hash) => {
    setServicesOpen(false);
    if (location.pathname === "/services") {
      const element = document.getElementById(hash);
      if (element) {
        const navbarHeight = 80;
        const elementPosition =
          element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: elementPosition - navbarHeight,
          behavior: "smooth",
        });
      }
    } else {
      navigate(`/services#${hash}`);
    }
  };

const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Travel Blog", path: "/travelblog" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      className="bg-[#d3f1ee] sticky top-0 z-50 border-b-2 border-black shadow-md"
    >
      <div className="w-full">
        <div className="flex items-center h-16 w-full justify-between md:justify-evenly px-4 md:px-0">
          {/* Left tagline - hidden on mobile/tablet */}
          <span className="hidden lg:block text-xl font-display font-bold text-[#008080] text-nowrap border-r-2 border-black px-8">
            Thoughtfully planned.
          </span>

          {/* Logo in elevated square box - using relative container for absolute positioning */}
          <div className="relative h-16 w-32 md:w-48">
            <Link
              to="/"
              className={`group absolute left-1/2 -translate-x-1/2 top-0 flex flex-col items-center justify-center bg-[#bf0000] text-[#f2f1ef] p-2 md:p-6 text-center transition-all duration-700 shadow-[0_8px_30px_rgba(191,0,0,0.4)] hover:shadow-[0_12px_40px_rgba(191,0,0,0.6)] hover:scale-[1.02] h-16 w-32 md:h-28 md:w-48`}
            >
              <span className="text-xl md:text-4xl font-display font-bold leading-normal text-nowrap transition-all duration-300 group-hover:tracking-wider">
                Maliya{window.innerWidth >= 768 && <br />} Travel Co.
              </span>
              <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-[2px] bg-[#f2f1ef] transition-all duration-300 group-hover:w-3/4 opacity-80"></span>
            </Link>
          </div>

          {/* Desktop Navigation - hidden on mobile */}
          <div className="hidden lg:flex items-center space-x-8 xl:space-x-16 border-x-2 border-black px-4 xl:px-8">
            {navLinks.map((link) =>
              link.name === "Services" ? (
                <div
                  key={link.path}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <Link
                    to={link.path}
                    className={`group relative text-lg xl:text-xl font-display font-bold text-nowrap transition-all duration-300 hover:scale-[1.02] hover:tracking-wider flex items-end gap-1 ${
                      isActive(link.path)
                        ? "text-black scale-[1.02]"
                        : "text-[#008080]"
                    }`}
                  >
                    {link.name}
                    <FaChevronDown
                      className={`w-3 h-3 mb-1 transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`}
                    />
                    <span
                      className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] bg-current transition-all duration-300 ${
                        isActive(link.path)
                          ? "w-full"
                          : "w-0 group-hover:w-full"
                      }`}
                    ></span>
                  </Link>
                  {servicesOpen && (
                    <div className="absolute top-full left-0 pt-2 z-50">
                      <div className="bg-[#d3f1ee] border-2 border-black shadow-lg py-2 min-w-[200px]">
                        {servicesSections.map((section) => (
                          <button
                            key={section.hash}
                            onClick={() => handleServiceClick(section.hash)}
                            className="block w-full text-left px-4 py-2 text-lg font-display font-bold text-[#008080] hover:bg-[#008080] hover:text-white transition-colors"
                          >
                            {section.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`group relative text-lg xl:text-xl font-display font-bold text-nowrap transition-all duration-300 hover:scale-[1.02] hover:tracking-wider ${
                    isActive(link.path)
                      ? "text-black scale-[1.02]"
                      : "text-[#008080]"
                  }`}
                >
                  {link.name}
                  <span
                    className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] bg-current transition-all duration-300 ${
                      isActive(link.path) ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  ></span>
                </Link>
              ),
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-gray-700 hover:text-primary-600 focus:outline-none"
          >
            {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>

          {/* Socials - hidden on small mobile */}
          <div className="hidden sm:flex space-x-4 items-center">
            <a
              href="https://www.instagram.com/maliyatravelco/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary-500 transition-colors"
            >
              <FaInstagram size={20} />
            </a>
            <a
              href="https://www.foratravel.com/advisor/maliya-reid"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src="/foraicon.png"
                alt="Fora Travel"
                className="w-8 h-8 rounded"
              />
            </a>
          </div>

          {/* Right tagline - hidden on mobile/tablet */}
          <span className="hidden lg:block text-xl font-display font-bold text-[#008080] text-nowrap border-l-2 border-black px-8">
            Effortlessly enjoyed.
          </span>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden pb-4 px-4 border-t-2 border-black">
            <div className="flex flex-col space-y-1 pt-4">
              {navLinks.map((link) =>
                link.name === "Services" ? (
                  <div key={link.path}>
                    <Link
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      className={`block font-display font-bold py-3 px-4 rounded transition-colors duration-200 text-lg ${
                        isActive(link.path)
                          ? "bg-[#008080] text-white"
                          : "text-[#008080] hover:bg-[#008080]/10"
                      }`}
                    >
                      {link.name}
                    </Link>
                    <div className="pl-4 space-y-1">
                      {servicesSections.map((section) => (
                        <button
                          key={section.hash}
                          onClick={() => {
                            handleServiceClick(section.hash);
                            setIsOpen(false);
                          }}
                          className="block w-full text-left py-2 px-4 text-base font-display text-gray-700 hover:text-[#008080] transition-colors"
                        >
                          {section.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`block font-display font-bold py-3 px-4 rounded transition-colors duration-200 text-lg ${
                      isActive(link.path)
                        ? "bg-[#008080] text-white"
                        : "text-[#008080] hover:bg-[#008080]/10"
                    }`}
                  >
                    {link.name}
                  </Link>
                ),
              )}
            </div>
            {/* Mobile social icons */}
            <div className="flex sm:hidden justify-center space-x-6 pt-4 mt-4 border-t border-[#008080]/20">
              <a
                href="https://www.instagram.com/maliyatravelco/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#008080] hover:text-primary-500 transition-colors"
              >
                <FaInstagram size={24} />
              </a>
              <a
                href="https://www.foratravel.com/advisor/maliya-reid"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <img
                  src="/foraicon.png"
                  alt="Fora Travel"
                  className="w-8 h-8 rounded"
                />
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
